import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { RadioInput } from "sr/helpers/ui-components/company/RadioInput";
import { FormattedMessage, useIntl } from "react-intl";
import { getGeneralType } from "sr/utils/api/getGeneralType";
import { ReactSelect } from "sr/helpers/ui-components/ReactSelect";
import { TrainingCertificationRequirementWarning } from "sr/helpers/ui-components/company/TrainingCertificationRequirementWarning";

type SelectOption = { value: string; label: string };

const TrainingCertificationChange: ForwardRefRenderFunction<any, any> = (
  props,
  ref,
) => {
  const intl = useIntl();
  const [requireCertificationTraining, setRequireCertificationTraining] =
    useState(false);
  const [certifications, setCertifications] = useState<string[]>([]);
  const [trainings, setTrainings] = useState<string[]>([]);
  const [trainingOptions, setTrainingOptions] = useState<SelectOption[]>([]);
  const [certificationOptions, setCertificationOptions] = useState<SelectOption[]>([]);
  const [error, setError] = useState("");

  const mapGeneralTypeOptions = (items: any[] = []) => {
    return items
      .map((item) => ({
        value: String(
          item?._id || item?.id || item?.value || item?.title || "",
        ),
        label: String(item?.title || item?.label || item?._id || item?.id || ""),
      }))
      .filter((opt) => {
        if (!opt.value) return false;
        const valueNormalized = String(opt.value).trim().toLowerCase();
        const labelNormalized = String(opt.label).trim().toLowerCase();
        if (valueNormalized === "other" || labelNormalized === "other") return false;
        return true;
      });
  };

  useEffect(() => {
    const fetchTrainingCertificationOptions = async () => {
      try {
        const [trainingResponse, certificationResponse] = await Promise.all([
          getGeneralType("cleanerTrainings"),
          getGeneralType("cleanerCertifications"),
        ]);

        setTrainingOptions(mapGeneralTypeOptions(trainingResponse?.data || []));
        setCertificationOptions(
          mapGeneralTypeOptions(certificationResponse?.data || []),
        );
      } catch (e) {
        void e;
      }
    };

    fetchTrainingCertificationOptions();
  }, []);

  useEffect(() => {
    setRequireCertificationTraining(
      Boolean(props?.data?.require_certification_training),
    );
    setCertifications(
      Array.isArray(props?.data?.certifications)
        ? props.data.certifications.map((item: any) => String(item)).filter(Boolean)
        : [],
    );
    setTrainings(
      Array.isArray(props?.data?.trainings)
        ? props.data.trainings.map((item: any) => String(item)).filter(Boolean)
        : [],
    );
    setError("");
  }, [props?.data]);

  const selectedTrainingOptions = useMemo(
    () => trainingOptions.filter((opt) => trainings.includes(opt.value)),
    [trainingOptions, trainings],
  );
  const selectedCertificationOptions = useMemo(
    () => certificationOptions.filter((opt) => certifications.includes(opt.value)),
    [certificationOptions, certifications],
  );

  useEffect(() => {
    props?.HandleDataProps?.({
      require_certification_training: requireCertificationTraining,
      trainings,
      certifications,
    });
  }, [requireCertificationTraining, trainings, certifications]);

  useImperativeHandle(ref, () => ({
    validateData: () => {
      if (!requireCertificationTraining) {
        setError("");
        return true;
      }

      if (trainings.length > 0 || certifications.length > 0) {
        setError("");
        return true;
      }

      setError("INPUT.VALIDATION.TRAINING.SELECTION");
      return false;
    },
  }));

  const handleTrainingChange = (selected: any) => {
    const selectedIds = Array.isArray(selected)
      ? selected.map((s) => String(s?.value || "")).filter(Boolean)
      : [];
    setTrainings(selectedIds);
    if (error) setError("");
  };

  const handleCertificationChange = (selected: any) => {
    const selectedIds = Array.isArray(selected)
      ? selected.map((s) => String(s?.value || "")).filter(Boolean)
      : [];
    setCertifications(selectedIds);
    if (error) setError("");
  };

  const handleRequireChange = (value: boolean) => {
    setRequireCertificationTraining(value);
    if (!value) {
      setTrainings([]);
      setCertifications([]);
      setError("");
    }
  };

  return (
    <div className="pt-8">
      <p className="text-base">
        <FormattedMessage
          id="RECRUITER.POSTJOB.REQUIRED.TRAINING_CERTIFICATION.TITLE"
          defaultMessage="Any required trainings or certifications?"
        />
      </p>

      <RadioInput
        name="requiredTrainingCertYes"
        value={true}
        checked={requireCertificationTraining === true}
        onChange={() => handleRequireChange(true)}
        label={
          intl.formatMessage({
            id: "RECRUITER.POSTJOB.REQUIRED.TRAINING_CERTIFICATION.YES",
            defaultMessage:
              "Yes, cleaners that apply must have specific trainings or certifications",
          })
        }
        inputcss="border border-zinc-800 h-11 rounded-lg my-2"
      />
      <RadioInput
        name="requiredTrainingCertNo"
        value={false}
        checked={requireCertificationTraining === false}
        onChange={() => handleRequireChange(false)}
        label={intl.formatMessage({
          id: "RECRUITER.POSTJOB.REQUIRED.TRAINING_CERTIFICATION.NO",
          defaultMessage: "No",
        })}
        inputcss="border border-zinc-800 h-11 rounded-lg my-2"
      />

      {requireCertificationTraining && (
        <div className="mt-4">
          <ReactSelect
            key={`training-${trainingOptions.length}-${trainings.join(",")}`}
            isMulti={true}
            isSearchable={true}
            error={error}
            InputDivClass="mt-0"
            labelClass="text-base mb-2 block"
            label={intl.formatMessage({
              id: "RECRUITER.POSTJOB.REQUIRED.TRAINING_CERTIFICATION.TRAININGS",
              defaultMessage: "Required trainings",
            })}
            onChange={handleTrainingChange}
            options={trainingOptions}
            defaultValue={selectedTrainingOptions}
            placeholder={intl.formatMessage({
              id: "RECRUITER.POSTJOB.REQUIRED.TRAINING_CERTIFICATION.TRAININGS.PLACEHOLDER",
              defaultMessage: "Select trainings",
            })}
          />

          <div className="mt-4">
            <ReactSelect
              key={`certification-${certificationOptions.length}-${certifications.join(",")}`}
              isMulti={true}
              isSearchable={true}
              error={error}
              InputDivClass="mt-0"
              labelClass="text-base mb-2 block"
              label={intl.formatMessage({
                id: "RECRUITER.POSTJOB.REQUIRED.TRAINING_CERTIFICATION.CERTIFICATIONS",
                defaultMessage: "Required certifications",
              })}
              onChange={handleCertificationChange}
              options={certificationOptions}
              defaultValue={selectedCertificationOptions}
              placeholder={intl.formatMessage({
                id: "RECRUITER.POSTJOB.REQUIRED.TRAINING_CERTIFICATION.CERTIFICATIONS.PLACEHOLDER",
                defaultMessage: "Select certifications",
              })}
            />
          </div>

          {error && (
            <div className="mt-4">
              <TrainingCertificationRequirementWarning />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default forwardRef(TrainingCertificationChange);
